import React, { useMemo } from 'react';
import styled from "styled-components";
import Colours from '@shared/ui/atoms/Colours';
import moment, { Moment } from "moment";
import { css } from "styled-components";
import { DATE_FORMAT, daysBetween, isWeekend } from '@shared/utils/DateUtils';
import { SchengenUsage } from '@features/holidays/services/HolidayService';

const WEEK_WIDTH = 230;
const DAY_WIDTH = WEEK_WIDTH / 7;

// Days allowed in any rolling 180 day period.
export const SCHENGEN_DAY_ALLOWANCE = 90;

const ICON_PATH = '/assets/icons/outline-icons';

export function schengenPalette(used: number) {
  if (used > 80) {
    return {background: '#FFDDD2', border: '#D72148', text: '#D72148', bold: true, icon: `${ICON_PATH}/euro-red.svg`};
  } else if (used > 50) {
    return {background: '#FFF6A1', border: '#D69462', text: Colours.black, bold: true, icon: `${ICON_PATH}/euro-yellow.svg`};
  }
  return {background: '#E3FFAB', border: '#339548', text: Colours.black, bold: true, icon: `${ICON_PATH}/euro-green.svg`};
}

export function SchengenIcon(props: Props) {
  const {date, allUsages, used, weeksInMonth, firstDateOnCalendar} = props;
  const hide = useMemo(() => {
    if (used === 0) {
      const previousDate = moment(date).subtract(1, 'days').format(DATE_FORMAT);
      const previousDay = allUsages.find(au => au.date === previousDate);
      return previousDay && previousDay.daysUsed === 0;
    }
    const momentDate = moment(date);
    if (isWeekend(momentDate)) {
      return true;
    }
    if (momentDate.isAfter(firstDateOnCalendar.clone().add(weeksInMonth, 'weeks').subtract(1, 'days'))) {
      return true;
    }
    return false;
  }, [allUsages, date, firstDateOnCalendar, used, weeksInMonth]);

  const palette = useMemo(() => {
    const tier = schengenPalette(used);
    // The lowest tier sits on every working day, so it stays quiet against the calendar. The pill
    // under the name keeps the green.
    if (used <= 50) {
      return {...tier, background: Colours.white, border: Colours.lightGrey};
    }
    return tier;
  }, [used]);

  if (hide) {
    return null;
  }
  return (
    <Wrapper colour={palette.background}
             borderColour={palette.border}
             textColour={palette.text}
             bold={palette.bold}
             showAlert={used >= 90}
             start={daysBetween(props.date, props.firstDateOnCalendar)}>
      {props.used}
    </Wrapper>
  )
}

interface Props {
  used: number;
  date: string;
  weeksInMonth: number
  firstDateOnCalendar: Moment;
  allUsages: SchengenUsage[];
}

const Wrapper = styled.div<any>`
  ${props => css`left: ${((props.start * (DAY_WIDTH) + 16) + WEEK_WIDTH) + 'px'};`};
  background-color: ${props => props.colour};
  position: absolute;
  border: 1px solid ${props => props.borderColour};
  border-radius: 100%;
  box-sizing: border-box;
  color: ${props => props.textColour};
  ${props => css`font-family: ${props.soleil ? '"soleil", sans-serif' : 'proxima-nova'};`};
  font-size: 12px;
  text-align: center;
  user-select: none;
  width: ${props => props.showAlert ? '25px' : '20px'};
  height: ${props => props.showAlert ? '25px' : '20px'};
  font-weight: ${props => (props.showAlert || props.bold) ? 'bold' : 'normal'};
  top: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
`
